import { Link } from '@tanstack/react-router';
import { CheckCircle2, FileText, Mail, XCircle } from 'lucide-react';
import { useState } from 'react';
import { CHANNEL_LABELS, type Channel } from '@marketingbee/contracts';
import { Alert, Badge, Button, Card, CardContent, ConfirmDialog, Field, FormError, Input, PageHeader, formatDateTime, useToast } from '@marketingbee/ui';
import { QueryView } from '../components/common';
import { useActiveBrand, useCanManageContent } from '../components/marketing';
import { errorMessage, trpc, type RouterOutputs } from '../lib/trpc';

type Connection = RouterOutputs['channels']['list'][number];

const INTRO: Record<Connection['kind'], { title: string; covers: string; how: string }> = {
  konekto_post: {
    title: 'Konekto Post',
    covers: 'Instagram, Facebook and LinkedIn',
    how: 'Create a free Konekto Post account at app.konekto.io, connect your Instagram, Facebook and LinkedIn there, then create a Personal Access Token (Konekto Post → Settings → Personal Access Tokens) and paste it here.',
  },
  bluesky: {
    title: 'Bluesky',
    covers: 'Bluesky',
    how: 'Create an app password in Bluesky (Settings → Privacy and security → App passwords) and enter it with your handle. Never use your main password.',
  },
};

export function ChannelsPage() {
  const list = trpc.channels.list.useQuery();
  const { brand } = useActiveBrand();
  return (
    <div className="space-y-6">
      <PageHeader title="Channels" description="Where MarketingBee publishes once you approve a post. Credentials are encrypted and only used to publish." />
      <QueryView query={list} label="Loading channels">
        {(rows) => (
          <div className="grid gap-4 lg:grid-cols-2">
            {rows.map((c) => (
              <ConnectionCard key={c.kind} conn={c} />
            ))}
          </div>
        )}
      </QueryView>
      <h2 className="pt-2 text-[18px] font-semibold tracking-tight">Built in</h2>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 py-6">
            <div className="flex items-center gap-3">
              <Mail className="size-5 text-muted-foreground" aria-hidden />
              <h3 className="text-[17px] font-semibold">Email</h3>
              {brand?.profile.mailingAddress.trim() ? <Badge variant="success">Ready</Badge> : <Badge variant="warning">Needs a mailing address</Badge>}
            </div>
            <p className="text-[14px] text-muted-foreground">Sent by MarketingBee to contacts who opted in. Every email carries your brand's mailing address and a one-click unsubscribe, as Canadian anti-spam law (CASL) requires.</p>
            {!brand?.profile.mailingAddress.trim() ? (
              <Button asChild size="sm" variant="outline">
                <Link to="/brand">Add the mailing address</Link>
              </Button>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-3 py-6">
            <div className="flex items-center gap-3">
              <FileText className="size-5 text-muted-foreground" aria-hidden />
              <h3 className="text-[17px] font-semibold">Blog</h3>
              <Badge variant="info">Export</Badge>
            </div>
            <p className="text-[14px] text-muted-foreground">Approved blog posts are exported as Markdown for you to publish on your own site.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ConnectionCard({ conn }: { conn: Connection }) {
  const intro = INTRO[conn.kind];
  const canManage = useCanManageContent();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const connect = trpc.channels.connect.useMutation();
  const test = trpc.channels.test.useMutation();
  const disconnect = trpc.channels.disconnect.useMutation();
  const [values, setValues] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState(false);
  const [lastResult, setLastResult] = useState<{ ok: boolean; message: string } | null>(null);
  const changed = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== ''));
  const missingRequired = !conn.connected && conn.spec.fields.some((f) => f.required && !changed[f.name]);

  const refresh = () => Promise.all([utils.channels.list.invalidate(), utils.workspace.current.invalidate()]);

  return (
    <Card>
      <CardContent className="space-y-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-[17px] font-semibold">{intro.title}</h3>
            <p className="text-[13px] text-muted-foreground">Publishes to {intro.covers}</p>
          </div>
          {conn.connected ? <Badge variant="success">Connected</Badge> : <Badge variant="outline">Not connected</Badge>}
        </div>
        <p className="text-[14px] text-muted-foreground">{intro.how}</p>

        {conn.connected && conn.kind === 'konekto_post' ? (
          <div className="flex flex-wrap items-center gap-2 text-[13px]">
            <span className="text-muted-foreground">Connected in Konekto Post:</span>
            {conn.platforms.length ? conn.platforms.map((p) => <Badge key={p}>{CHANNEL_LABELS[p as Channel] ?? p}</Badge>) : <span>none yet. Connect them in Konekto Post, then test again.</span>}
          </div>
        ) : null}

        {conn.lastTestAt ? (
          <p className="flex items-start gap-2 text-[13px]">
            {conn.lastTestOk ? <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-label="Passed" /> : <XCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-label="Failed" />}
            <span>
              Last test {formatDateTime(conn.lastTestAt)}: {conn.lastTestMessage ?? (conn.lastTestOk ? 'OK' : 'Failed')}
            </span>
          </p>
        ) : null}
        {lastResult ? <Alert variant={lastResult.ok ? 'success' : 'destructive'}>{lastResult.message}</Alert> : null}
        {connect.error ? <FormError message={errorMessage(connect.error)} /> : null}

        {canManage ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              {conn.spec.fields.map((f) => {
                const hint = conn.hints?.[f.name];
                return (
                  <Field key={f.name} label={f.label} required={f.required && !conn.connected} description={f.help ?? (f.secret && hint ? `Saved, ends in ${hint}. Leave blank to keep it.` : f.secret && conn.connected ? 'Saved. Leave blank to keep it.' : undefined)}>
                    <Input
                      type={f.secret ? 'password' : 'text'}
                      autoComplete="off"
                      value={values[f.name] ?? ''}
                      placeholder={f.secret ? (hint ? `•••• ${hint}` : f.placeholder) : (hint ?? f.placeholder)}
                      aria-label={`${intro.title} ${f.label}`}
                      onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                    />
                  </Field>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={Object.keys(changed).length === 0 || missingRequired}
                loading={connect.isPending}
                onClick={() =>
                  connect.mutate(
                    { kind: conn.kind, fields: changed },
                    {
                      onSuccess: async (r) => {
                        success(conn.connected ? `${intro.title} updated` : `${intro.title} connected`, r.message);
                        setValues({});
                        setLastResult({ ok: true, message: r.message });
                        await refresh();
                      },
                    },
                  )
                }
              >
                {conn.connected ? 'Update' : 'Connect'}
              </Button>
              {conn.connected ? (
                <>
                  <Button
                    variant="outline"
                    loading={test.isPending}
                    onClick={() =>
                      test.mutate(
                        { kind: conn.kind },
                        {
                          onSuccess: async (r) => {
                            setLastResult({ ok: r.ok, message: r.message });
                            await refresh();
                          },
                          onError: (e) => error('Test failed', errorMessage(e)),
                        },
                      )
                    }
                  >
                    Test
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirm(true)}>
                    Disconnect
                  </Button>
                </>
              ) : null}
            </div>
          </>
        ) : (
          <p className="text-[13px] text-muted-foreground">Ask an owner or manager to change connections.</p>
        )}
      </CardContent>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`Disconnect ${intro.title}?`}
        description={`Scheduled posts for ${intro.covers} will not publish until you connect again. The saved credentials are deleted.`}
        confirmLabel="Disconnect"
        destructive
        loading={disconnect.isPending}
        onConfirm={() =>
          disconnect.mutate(
            { kind: conn.kind },
            {
              onSuccess: async () => {
                success(`${intro.title} disconnected`);
                setConfirm(false);
                setLastResult(null);
                await refresh();
              },
              onError: (e) => error('Could not disconnect', errorMessage(e)),
            },
          )
        }
      />
    </Card>
  );
}
